import { Timeline } from 'antd';
import dayjs from 'dayjs';

import { DECISION_MAKER_BADGE } from './DecisionMakerBadge';
import DecisionTypeBadge from './DecisionTypeBadge';

/**
 * @description antd preset renk adı → o presetin `-7` (koyu / metin), `-1` (açık / zemin) ve
 * `-3` (kenarlık) tonları. Birim renkleri tek kaynaktan gelir: `DECISION_MAKER_BADGE[unit].color`
 * bir antd preset adı verir, burada da o presetin timeline'da kullanılan üç tonuna çevrilir.
 * Böylece "birime göre renk" kararı hâlâ tek yerde (DecisionMakerBadge) tutulur.
 * @type {Record<string, { dark: string, light: string, border: string }>}
 */
const PRESET_PALETTE = {
  green: { dark: '#389e0d', light: '#f6ffed', border: '#b7eb8f' },
  geekblue: { dark: '#1d39c4', light: '#f0f5ff', border: '#adc6ff' },
  blue: { dark: '#096dd9', light: '#e6f7ff', border: '#91d5ff' },
  purple: { dark: '#531dab', light: '#f9f0ff', border: '#d3adf7' },
  cyan: { dark: '#08979c', light: '#e6fffb', border: '#87e8de' },
  magenta: { dark: '#c41d7f', light: '#fff0f6', border: '#ffadd2' },
  gold: { dark: '#d48806', light: '#fffbe6', border: '#ffe58f' },
  volcano: { dark: '#d4380d', light: '#fff2e8', border: '#ffbb96' },
};

/** Eşleşmeyen (henüz bağlanmamış) birim için nötr gri palet. */
const FALLBACK_PALETTE = { dark: '#595959', light: '#fafafa', border: '#d9d9d9' };

/**
 * Bir decider (birim) değerini timeline satırının etiketi + renk paletine çözer.
 * @param {string} unit BE `decisionMaker` / decider değeri.
 * @returns {{ label: string, dark: string, light: string, border: string }}
 */
const resolveUnit = (unit) => {
  const badge = DECISION_MAKER_BADGE[unit];
  const palette = (badge && PRESET_PALETTE[badge.color]) || FALLBACK_PALETTE;
  return { label: badge?.label || 'Birim', ...palette };
};

/**
 * @typedef {Object} MediaEvaluation
 * @property {number|string} id Benzersiz anahtar.
 * @property {string} unit Değerlendirmeyi yapan birim (BE `decisionMaker` / `MEDIA_OFFER_DECIDER`).
 * @property {string} note Değerlendirme metni (BE `decisionNote`).
 * @property {'approved'|'not_approved'|null} [decisionType] Karar tipi (BE `decisionType`).
 *   Tip yoksa (ör. yalnızca not bırakan limitedEdit) rozet gösterilmez.
 * @property {string|null} person Değerlendirmeyi yapan kişinin adı soyadı (BE `decidedByUser.fullName`).
 *   Eski kararlarda `null` olabilir; o durumda "-" gösterilir.
 * @property {string} date ISO tarih; oluşturulma zamanı (BE `createdAt`).
 */

/**
 * @description Tek bir medya teklifi grubuna ait değerlendirmelerin (karar + not) zaman çizelgesi.
 * En yeniden en eskiye, her satır: solda birime göre renkli nokta; sağda birim renginde tonlanmış
 * kart (karar rozeti + değerlendirme metni + `Birim / Kişi / Tarih` alt bilgisi). Yalnız telif sonrası karar
 * modalında render edilir (kapı çağıran tarafta, `isAfterCopyrightDecider` ile).
 *
 * Salt-sunumdur: veriyi `evaluations` prop'undan alır, kendi fetch'i yoktur — çağıran taraf
 * (`SupervisorEditMediaContent`) grubun `currentVersion.decisions` dizisini bu şekle map'ler.
 *
 * @param {Object} props
 * @param {MediaEvaluation[]} [props.evaluations=[]] Gösterilecek değerlendirmeler (en yeni → en eski).
 * @returns {JSX.Element|null} Değerlendirme yoksa `null`.
 */
function MediaEvaluationTimeline({ evaluations = [] }) {
  if (evaluations.length === 0) return null;

  return (
    <div className="media-evaluation-timeline gx-mt-3">
      <h4 className="gx-mb-3">Değerlendirmeler</h4>
      <Timeline>
        {evaluations.map((evaluation) => {
          const { label, dark, light, border } = resolveUnit(evaluation.unit);
          return (
            <Timeline.Item
              key={evaluation.id}
              dot={
                <span
                  className="media-evaluation-timeline__dot"
                  style={{ borderColor: dark }}
                />
              }
            >
              <div
                className="media-evaluation-card"
                style={{ backgroundColor: light, borderColor: border }}
              >
                <div className="media-evaluation-card__header">
                  <div
                    className="media-evaluation-card__label"
                    style={{ color: dark }}
                  >
                    Değerlendirme
                  </div>
                  {evaluation.decisionType ? (
                    <DecisionTypeBadge text={evaluation.decisionType} />
                  ) : null}
                </div>
                <p className="media-evaluation-card__note">{evaluation.note || '-'}</p>
                <div className="media-evaluation-card__meta">
                  <span style={{ color: dark, fontWeight: 600 }}>{label}</span>
                  <span className="media-evaluation-card__sep">/</span>
                  <span>{evaluation.person || '-'}</span>
                  <span className="media-evaluation-card__sep">/</span>
                  <span>{evaluation.date ? dayjs(evaluation.date).format('DD/MM/YYYY HH:mm') : '-'}</span>
                </div>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </div>
  );
}

export default MediaEvaluationTimeline;
